import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { getApiUrl, getAppPlatformUrl } from "@dashboard/config";
import useNotifier from "@dashboard/hooks/useNotifier";
import { getStaffAccessToken } from "@dashboard/services/saleorStaffToken";
import React from "react";
import { useIntl } from "react-intl";

/** Mirrors MAX_STAFF_NOTE_LENGTH in the SMTP app (external_apps/smtp/views.py). */
export const MAX_STAFF_NOTE_LENGTH = 2000;

interface UseOrderSendEmailProps {
  orderId: string;
  onSent: () => void;
}

/**
 * Sends the "Send Email" note for an order through the SMTP app's API
 * (WSM6-2799).
 *
 * The request carries an order id and a short plain-text note and nothing else:
 * the subject, the body and the recipient are all rendered server-side from the
 * tenant's own staff-editable template. The dashboard used to compose HTML and
 * post it here, which made this endpoint an arbitrary-content relay under the
 * merchant's verified sender identity; that is why the body shrank to this.
 *
 * The app verifies the staff session itself, so it needs the Saleor token as a
 * bearer credential plus the tenant's API URL — it is a separate origin and has
 * no other way to know which tenant is calling.
 */
export const useOrderSendEmail = ({ orderId, onSent }: UseOrderSendEmailProps) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [status, setStatus] = React.useState<ConfirmButtonTransitionState>("default");
  const [error, setError] = React.useState<string | null>(null);
  // The server mints a fresh idempotency key per request on purpose, so a
  // genuine "fix a typo and resend" works. That makes suppressing an accidental
  // double-send the caller's job: the dialog disables the button while a
  // request is in flight, and this ref closes the gap between two clicks
  // landing in the same render.
  const inFlight = React.useRef(false);
  const reset = React.useCallback(() => {
    setStatus("default");
    setError(null);
  }, []);
  const send = React.useCallback(
    async (note: string) => {
      if (inFlight.current) {
        return;
      }

      inFlight.current = true;
      setStatus("loading");
      setError(null);

      const fail = (message: string) => {
        setStatus("error");
        setError(message);
        notify({ status: "error", text: message });
      };

      try {
        const token = await getStaffAccessToken();
        const response = await fetch(`${getAppPlatformUrl("smtp")}/api/send-email`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            "X-Saleor-Api-Url": getApiUrl(),
          },
          body: JSON.stringify({ orderId, note }),
        });
        // Every failure carries operator-facing copy the app wrote for this
        // screen, so surface it verbatim rather than mapping status codes to
        // our own strings. 401 and 429 come from the shared auth decorator and
        // use `error` instead of `message`.
        const result = await response.json().catch(() => null);
        const serverMessage = result?.message || result?.error;

        if (response.ok && result?.status === "success") {
          setStatus("success");
          notify({
            status: "success",
            text: intl.formatMessage({
              id: "8/qhdz",
              defaultMessage: "Email sent successfully",
            }),
          });
          onSent();

          return;
        }

        fail(
          serverMessage ||
            intl.formatMessage({
              id: "TM0J6v",
              defaultMessage: "Failed to send email",
            }),
        );
      } catch {
        fail(
          intl.formatMessage({
            id: "MKqtO+",
            defaultMessage: "Failed to send email. Please try again.",
          }),
        );
      } finally {
        inFlight.current = false;
      }
    },
    [intl, notify, onSent, orderId],
  );

  return { status, error, send, reset };
};
